import { addYears, format, parseISO, startOfDay } from "date-fns";
import { queryClient } from "@/queryClient";
import { BOOKINGS_QUERY_KEY } from "@/services/bookings/bookings";
import { type Customer } from "@/services/bookings/customers";

/** Every storage query sits under this key; every storage mutation invalidates it. */
export const STORAGE_QUERY_KEY = "storage";

/**
 * After any change to a storage booking: the storage views, and the bookings
 * list and booking dialog (a storage booking is a Booking).
 */
export const invalidateStorageQueries = () => {
  void queryClient.invalidateQueries([STORAGE_QUERY_KEY]);
  void queryClient.invalidateQueries([BOOKINGS_QUERY_KEY]);
};

export type StorageKind = "caravan" | "motorhome" | "trailer_tent" | "other";
export type StorageLocation = "in" | "out";
export type StorageBookingState =
  | "current"
  | "upcoming"
  | "ended"
  | "cancelled";

export const STORAGE_KIND_LABELS: Record<StorageKind, string> = {
  caravan: "Caravan",
  motorhome: "Motorhome",
  trailer_tent: "Trailer tent",
  other: "Other",
};

// Keep in sync with StorageCustomerSchema (apps/storage/api.py)
export type StorageCustomer = Omit<Customer, "title"> & {
  title: string | null;
  name: string;
};

// Keep in sync with LastMovementSchema
export type StorageLastMovement = {
  at: string;
  direction: StorageLocation;
  laneName: string;
  via: "pin" | "companion" | "";
  registration: string;
  photoUrl: string | null;
};

// Keep in sync with OutfitSchema
export type StorageOutfit = {
  bookingId: string;
  bookingRef: string;
  customer: StorageCustomer | null;
  kind: StorageKind;
  location: StorageLocation;
  locationChangedAt: string | null;
  /** The primary vehicle (a motorhome's own plate) first. */
  registrations: string[];
  startDate: string;
  endDate: string;
  ended: boolean;
  cancelled: boolean;
  state: StorageBookingState;
  lastMovement: StorageLastMovement | null;
};

export type TouringBookingRef = { id: string; pitchNumber: string | null };

export type StorageSummaryOutfit = StorageOutfit & {
  touringBookingHere: TouringBookingRef | null;
};

// Keep in sync with StoragePitchSchema
export type StoragePitch = {
  id: string;
  name: string;
  mapX: number;
  mapY: number;
  outfit: StorageSummaryOutfit | null;
};

// Keep in sync with LaneSchema: one gate kiosk. A vehicle lane has a camera.
export type StorageLane = {
  id: string;
  name: string;
  kind: "vehicle" | "pedestrian";
  direction: StorageLocation;
  cameraId: string | null;
  lastSeenAt: string | null;
};

// Keep in sync with CompoundSchema
export type StorageCompound = {
  siteId: string;
  hours: string;
  opensAt: string;
  closesAt: string;
  confirmWindowSeconds: number;
  mapImageUrl: string | null;
  pitches: StoragePitch[];
  lanes: StorageLane[];
};

export type StoragePitchRef = { id: string; name: string };

// Keep in sync with StorageBookingSchema
export type StorageBooking = StorageOutfit & {
  pitch: StoragePitchRef;
  siteId: string;
  note: string;
  hasPin: boolean;
  touringBookingHere: TouringBookingRef | null;
};

// Keep in sync with StorageBookingInSchema
export type StorageBookingInput = {
  customerId: string;
  pitchId: string;
  kind: StorageKind;
  registrations: string[];
  startDate: string;
  endDate: string;
  note: string;
};

// Keep in sync with CrossingSchema
export type StorageCrossing = {
  id: string;
  at: string;
  resolvedAt: string | null;
  siteId: string;
  bookingId: string | null;
  customerName: string | null;
  pitchName: string | null;
  direction: StorageLocation;
  laneName: string;
  laneKind: "vehicle" | "pedestrian";
  status: "pending" | "opened" | "denied" | "expired" | "superseded";
  denyReason: string;
  via: "pin" | "companion" | "";
  registration: string;
  photoUrl: string | null;
  locationBefore: StorageLocation | "";
  locationAfter: StorageLocation | "";
};

/**
 * Where a storage booking's unit is, as the bookings list, the booking dialog
 * and the compound map show it. "Ended, still in" is the one to act on.
 */
export type StorageMarkerState = "in" | "out" | "endedIn" | "vacant";

export const storageMarkerState = (
  outfit: Pick<StorageOutfit, "location" | "ended"> | null,
): StorageMarkerState => {
  if (!outfit) return "vacant";
  if (outfit.location === "in") return outfit.ended ? "endedIn" : "in";
  return "out";
};

/** Words and chip colour (a windi colour, as BOOKING_STATUS_MAP) per state;
 *  `ring` and `flash` draw it on the compound map like the site plan's pitches. */
export const STORAGE_STATE_META: Record<
  StorageMarkerState,
  { label: string; color: string; ring: string; flash: boolean }
> = {
  vacant: { label: "Available", color: "green", ring: "#1f9d55", flash: true },
  in: { label: "In", color: "orange", ring: "#f97316", flash: false },
  out: { label: "Out", color: "gray2", ring: "#57534e", flash: false },
  endedIn: {
    label: "Ended, still in",
    color: "red",
    ring: "#e23b3b",
    flash: true,
  },
};

export const STORAGE_STATE_ORDER: StorageMarkerState[] = [
  "vacant",
  "in",
  "out",
  "endedIn",
];

export const storageCustomerAsCustomer = (
  customer: StorageCustomer,
): Customer => ({
  ...customer,
  title: customer.title ?? "",
});

export const storageVia = (via: StorageCrossing["via"]): string =>
  via === "pin" ? "PIN" : via === "companion" ? "Companion" : "";

/** "1 Jul 2026" from an ISO date or datetime. */
export const formatStorageDate = (iso: string): string =>
  format(parseISO(iso), "d MMM yyyy");

/** "3 Jul 2026, 14:32" from an ISO datetime, in the browser's time zone. */
export const formatStorageDateTime = (iso: string): string =>
  format(parseISO(iso), "d MMM yyyy, HH:mm");

/** The end date a renewal without a chosen date gets, as the server works it
 *  out: a year on from the current end, or from today if that is later. */
export const defaultStorageRenewalDate = (endDate: string): string => {
  const end = parseISO(endDate);
  const today = startOfDay(new Date());
  return format(addYears(end > today ? end : today, 1), "yyyy-MM-dd");
};
